import { isAbortError } from 'cadenza/api-client/abort-controller/abort-error';
import { Command } from 'cadenza/api-client/workbook-commands';
import { deleteWorkOrders } from 'cadenza/api-client/workorder-api';
import { objecttypeIdFromString } from 'cadenza/repository/model/objecttype-id';
import { getStore } from 'cadenza/store/cadenza-store';
import { assertNonNullable, assertUnreachable } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import type { EnrichmentRequest, ObjecttypeDataScopeMapping } from 'cadenza/workbook/api/enrichment-api';
import { startEnrichment } from 'cadenza/workbook/api/enrichment-api';
import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { confirmSavePublishedWorkbookBeforeAction } from 'cadenza/workbook/confirm-save-and-execute-dialog/confirm-save-published-workbook';
import { showEnrichmentDialog } from 'cadenza/workbook/enrichment/enrichment-dialog/create-enrichment-dialog';
import { showEnrichmentProgressDialog } from 'cadenza/workbook/enrichment/enrichment-progress-dialog';
import {
  createEnrichmentErrorAlert,
  getAttributeIdFromWorkbookAttributeDto,
  openEnrichmentReportWorksheet,
} from 'cadenza/workbook/enrichment/enrichment-utils';
import type { EnrichmentCandidateDto } from 'cadenza/workbook/enrichment/model/enrichment-candidate-dto';
import { getAsyncEnrichmentRuleCheckerOptions } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-rule-checker';
import {
  executeCommand,
  fetchDataDependencyState,
  getActiveWorksheetId,
  getWorkbookNameAndHash,
} from 'cadenza/workbook/workbook.state';

export async function createEnrichment(
  objecttypeToEnrich: Objecttype,
  enrichmentCandidate: EnrichmentCandidateDto,
  targetAttribute?: WorkbookAttributeDto,
) {
  // 1) if the workbook is under publishing control, then the workbook may have to be saved which the user has to confirm first
  if (await showWarningDialogBeforeCreatingEnrichment(objecttypeToEnrich)) {
    const confirmation = await confirmSavePublishedWorkbookBeforeAction();
    if (!confirmation) {
      return;
    }
  }

  // 2) user configures enrichment parameters
  const dialogResult = await connectAndShowCreateEnrichmentDialog(
    enrichmentCandidate,
    objecttypeToEnrich,
    targetAttribute,
  );
  if (!dialogResult) {
    return;
  }

  // 3) execute the enrichment
  let enrichmentResult;
  try {
    enrichmentResult = await executeEnrichmentRequest(enrichmentCandidate.type, dialogResult);
  } catch (error) {
    if (!isAbortError(error)) {
      logger.error('Failed to execute Enrichment', error);
      await createEnrichmentErrorAlert();
    }
    return;
  }

  // 4) add the enrichment objecttype to the workbook
  const createdObjecttypeId = enrichmentResult.createdObjectTypeId;
  if (createdObjecttypeId !== objecttypeToEnrich.id) {
    await getStore().dispatch(executeCommand(Command.ADD_OBJECTTYPES({ objecttypeIds: [createdObjecttypeId] })));
    await getStore().dispatch(fetchDataDependencyState());
  }

  // 5) if available, navigate to report worksheet
  if (enrichmentResult.createdReportWorksheetId) {
    openEnrichmentReportWorksheet(enrichmentResult.createdReportWorksheetId);
  }
}

async function showWarningDialogBeforeCreatingEnrichment(objecttype: Objecttype) {
  const { repositoryName } = objecttypeIdFromString(objecttype.id);
  const enrichmentRuleCheckerOptions = await getAsyncEnrichmentRuleCheckerOptions(repositoryName);
  const workbookAndObjecttypeInDifferentRepositories =
    !enrichmentRuleCheckerOptions.workbookAndEnrichmentObjecttypeInTheSameRepository;
  return !(
    workbookAndObjecttypeInDifferentRepositories &&
    !enrichmentRuleCheckerOptions.enrichmentObjecttypeRepositoryHasPublishingEnabled
  );
}

function connectAndShowCreateEnrichmentDialog(
  enrichmentCandidate: EnrichmentCandidateDto,
  objecttypeToEnrich: Objecttype,
  attribute: WorkbookAttributeDto | undefined,
) {
  const baseOptions = { enrichedObjecttypeId: objecttypeToEnrich.id };

  switch (enrichmentCandidate.type) {
    case 'StructuredGeocodingEnrichment':
    case 'UnstructuredGeocodingEnrichment': {
      return showEnrichmentDialog(enrichmentCandidate.type, baseOptions);
    }
    case 'IpGeocodingEnrichment': {
      return showEnrichmentDialog('IpGeocodingEnrichment', {
        ...baseOptions,
        ipAddressAttribute: getAttributeIdFromWorkbookAttributeDto(attribute),
      });
    }
    case 'ReverseGeocodingEnrichment': {
      const enrichedAttributeId =
        getAttributeIdFromWorkbookAttributeDto(attribute) || enrichmentCandidate.attributeIds?.[0];
      assertNonNullable(enrichedAttributeId, 'attribute required');
      return showEnrichmentDialog('ReverseGeocodingEnrichment', {
        ...baseOptions,
        dataScopeSettings: getDataScopeSettings(objecttypeToEnrich),
        enrichedAttributeId,
      });
    }
    case 'MeasuresFromSpatialIntersectionEnrichment': {
      const enrichedAttributeId =
        getAttributeIdFromWorkbookAttributeDto(attribute) || enrichmentCandidate.attributeIds?.[0];
      assertNonNullable(enrichedAttributeId, 'attribute required');
      return showEnrichmentDialog('MeasuresFromSpatialIntersectionEnrichment', {
        ...baseOptions,
        enrichedAttributeId,
      });
    }
    case 'AnalyticsExtensionEnrichment': {
      const definition = { analyticsExtensionId: enrichmentCandidate.analyticsExtensionId };
      return showEnrichmentDialog('AnalyticsExtensionEnrichment', {
        ...baseOptions,
        analyticsExtensionId: definition.analyticsExtensionId,
      });
    }
    case 'IsochronesEnrichment': {
      return showEnrichmentDialog('IsochronesEnrichment', {
        ...baseOptions,
        enrichedAttributeId: getAttributeIdFromWorkbookAttributeDto(attribute) || enrichmentCandidate.attributeIds?.[0],
      });
    }
    default:
      assertUnreachable(enrichmentCandidate);
  }
}

function getDataScopeSettings(objecttypeToEnrich: Objecttype) {
  const dataScopes: ObjecttypeDataScopeMapping =
    objecttypeToEnrich.isCombinedObjecttype ?
      { [objecttypeToEnrich.underlyingBasicMainObjecttypeId]: 'ALL_DATA' }
    : { [objecttypeToEnrich.id]: 'ALL_DATA' };
  return {
    objecttypeDataScopeMapping: dataScopes,
    workbookNameAndHash: getWorkbookNameAndHash(getStore().getState()),
    worksheetId: getActiveWorksheetId(getStore().getState()),
  };
}

async function executeEnrichmentRequest(enrichmentType: EnrichmentType, request: EnrichmentRequest<EnrichmentType>) {
  let workOrderId;
  try {
    const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
    workOrderId = await startEnrichment(workbookNameAndHash, enrichmentType, request);
    return await showEnrichmentProgressDialog(enrichmentType, request.enrichedObjecttypeId, workOrderId);
  } finally {
    if (workOrderId) {
      await deleteWorkOrders([workOrderId]);
    }
  }
}
